/**
 * web/src/hooks/mutations/useArchiveWorkflow.ts
 * Soft-hide / restore a workflow. Open to any authenticated user on the server
 * (ops takes runs offline routinely). Unlike delete, archiving is reversible
 * and removes no data — the row just drops out of the default list + stat
 * counts until unarchived.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../lib/queryKeys';
import { toast } from 'sonner';
import { archiveWorkflow, unarchiveWorkflow } from '../../api/workflows';
import { toastApiError } from '../../lib/api';

/**
 * Archives or unarchives a workflow. Invalidates the workflow list + stats
 * caches on success so the row appears/disappears from the current view.
 *
 * @returns React Query mutation handle — mutate({ id, archived })
 * @sideEffects HTTP POST /api/workflows/:id/(archive|unarchive)
 * @idempotent yes — re-applying the same flag still 200s
 */
export function useArchiveWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      archived ? archiveWorkflow(id) : unarchiveWorkflow(id),
    onSuccess: (_data, { id, archived }) => {
      toast.success(archived ? 'Workflow archived' : 'Workflow restored');
      qc.invalidateQueries({ queryKey: qk.workflows.root() });
      qc.invalidateQueries({ queryKey: qk.stats.root() });
      qc.invalidateQueries({ queryKey: qk.workflow.root(id) });
    },
    onError: (err) => {
      toastApiError(err, 'Archive failed');
    },
  });
}
